import { Link } from 'react-router-dom';
import type { AwardResult, PayoutConfig } from '@ff/core';
import { money } from '../lib/format.ts';

/**
 * The side-pot board: Goon, Survivor, Heatbreaker and friends. Each is a small
 * chalk ticket. A settled award shows its winner; a live one shows the current
 * leader with a pulse; a pending one is dim.
 *
 * An award that has a page of its own becomes a link to it. That used to be a
 * tab; the tab is gone, and a pot nobody can click through to is a pot nobody
 * finds.
 */
export function AwardStrip({
  awards,
  config,
  nameOf,
}: {
  awards: AwardResult[];
  config: PayoutConfig;
  nameOf: (id: string) => string;
}) {
  // Which awards earn their own page is the league's call, not this component's.
  const hasPage = new Set(config.awards.filter((a) => a.page).map((a) => a.id));
  // Collapse the per-week payouts — fourteen of them — and show the season
  // pots, which are what people actually argue about. A weekly award is
  // exactly one that carries a week.
  const seasonAwards = awards.filter((a) => a.week === undefined);

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {seasonAwards.map((a) => (
        <AwardTicket
          key={a.id}
          award={a}
          href={hasPage.has(a.id) ? `/award/${a.id}` : null}
          nameOf={nameOf}
        />
      ))}
    </div>
  );
}

function AwardTicket({
  award,
  href,
  nameOf,
}: {
  award: AwardResult;
  href: string | null;
  nameOf: (id: string) => string;
}) {
  const winners = award.winnerIds.map(nameOf).join(' & ');
  const live = award.status === 'live';
  const pending = award.status === 'pending';

  const face = (
    <>
      <div className="flex items-center justify-between">
        <span className="eyebrow truncate">{award.label}</span>
        <span className="font-mono text-xs text-brass tabular">{money(award.amount)}</span>
      </div>
      <div className="mt-2">
        {pending ? (
          <span className="text-sm text-chalk-dim">TBD</span>
        ) : (
          <span className="font-display text-sm font-bold leading-tight">
            {winners || 'TBD'}
          </span>
        )}
      </div>
      <div className="mt-1 flex items-center gap-1.5">
        {live && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-cash" />}
        <span className="text-[0.7rem] text-chalk-dim">{award.detail}</span>
      </div>
    </>
  );

  const shape = 'card flex flex-col justify-between p-3';

  // A ticket with somewhere to go says so on hover; one without stays a card
  // rather than becoming a link that goes nowhere.
  return href ? (
    <Link to={href} className={`${shape} transition-colors hover:border-brass`}>
      {face}
    </Link>
  ) : (
    <div className={shape}>{face}</div>
  );
}
